import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Users, Clock, CheckCircle2, XCircle, AlertCircle, Eye, 
  ArrowLeft, Loader2, Search, Filter, FileText, Download, LogOut, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AdminDoctorLinks } from "@/components/AdminDoctorLinks";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  PENDING_REVIEW: { label: "Pendiente de revisión", color: "bg-amber-100 text-amber-700", icon: Clock },
  PENDING_PROFILE: { label: "Perfil incompleto", color: "bg-slate-100 text-slate-700", icon: AlertCircle },
  ACTIVE: { label: "Activo", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  REJECTED: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
  SUSPENDED: { label: "Suspendido", color: "bg-orange-100 text-orange-700", icon: AlertCircle },
  PENDING_EMAIL: { label: "Esperando verificación", color: "bg-blue-100 text-blue-700", icon: Clock }
};

const AdminDoctors = ({ user }) => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = statusFilter === "all" 
        ? `${API_URL}/api/admin/doctors`
        : `${API_URL}/api/admin/doctors?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors);
        setStats(data.stats);
      } else {
        toast.error("Error al cargar médicos");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  const handleApprove = async (doctor) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/doctors/${doctor.id}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: "approve" })
      });

      if (response.ok) {
        toast.success(`${doctor.name} aprobado correctamente`);
        setShowDetailModal(false);
        loadDoctors();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Error al aprobar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Debes proporcionar un motivo");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/doctors/${selectedDoctor.id}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: "reject", reason: rejectReason })
      });

      if (response.ok) {
        toast.success("Solicitud rechazada");
        setShowRejectModal(false);
        setShowDetailModal(false);
        setRejectReason("");
        loadDoctors();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Error al rechazar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (doctor) => {
    setSelectedDoctor(doctor);
    setShowRejectModal(true);
  };

  const openDetailModal = (doctor) => {
    setSelectedDoctor(doctor);
    setShowDetailModal(true);
  };

  // Filter doctors by search term
  const filteredDoctors = doctors.filter(d => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      d.name?.toLowerCase().includes(search) ||
      d.email?.toLowerCase().includes(search) ||
      d.license_number?.toLowerCase().includes(search) ||
      d.specialty?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Gestión de Médicos</h1>
                <p className="text-sm text-slate-500">Validación y administración de cuentas médicas</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/institution-reviews')}
                data-testid="institution-reviews-btn"
              >
                <Building2 className="h-4 w-4 mr-1" />
                Solicitudes de institución
              </Button>
              {user?.email && (
                <span className="hidden sm:inline text-xs text-slate-500" data-testid="admin-doctors-user-email">
                  {user.email}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  localStorage.removeItem('auth_token');
                  navigate('/');
                  toast.success("Sesión cerrada");
                }}
                data-testid="admin-doctors-logout-btn"
                className="text-slate-700 hover:text-red-600 hover:border-red-200"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg border p-4 border-l-4 border-l-amber-400">
              <p className="text-sm text-slate-500">Pendientes</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending_review}</p>
            </div>
            <div className="bg-white rounded-lg border p-4 border-l-4 border-l-green-400">
              <p className="text-sm text-slate-500">Activos</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="bg-white rounded-lg border p-4 border-l-4 border-l-red-400">
              <p className="text-sm text-slate-500">Rechazados</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </div>
            <div className="bg-white rounded-lg border p-4 border-l-4 border-l-orange-400">
              <p className="text-sm text-slate-500">Suspendidos</p>
              <p className="text-2xl font-bold text-orange-600">{stats.suspended}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email, matrícula o especialidad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="PENDING_REVIEW">Pendientes de revisión</SelectItem>
                  <SelectItem value="ACTIVE">Activos</SelectItem>
                  <SelectItem value="REJECTED">Rechazados</SelectItem>
                  <SelectItem value="SUSPENDED">Suspendidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No se encontraron médicos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Médico</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Especialidad</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Matrícula</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Links</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">País</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Registro</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDoctors.map((doctor) => {
                    const statusConfig = STATUS_CONFIG[doctor.doctor_status] || STATUS_CONFIG.PENDING_REVIEW;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr key={doctor.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{doctor.name}</p>
                            <p className="text-sm text-slate-500">{doctor.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {doctor.specialty || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-mono text-slate-900">{doctor.license_number || "-"}</p>
                            <p className="text-xs text-slate-500">{doctor.license_issuer || ""}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3"><AdminDoctorLinks doctor={doctor} /></td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {doctor.country || "-"}
                          {doctor.province && <span className="text-slate-400"> / {doctor.province}</span>}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${statusConfig.color} border-0`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(doctor.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDetailModal(doctor)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {doctor.doctor_status === 'PENDING_REVIEW' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApprove(doctor)}
                                  disabled={actionLoading}
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => openRejectModal(doctor)}
                                  disabled={actionLoading}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle del Médico</DialogTitle>
          </DialogHeader>
          
          {selectedDoctor && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Nombre</p>
                  <p className="font-medium">{selectedDoctor.name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium">{selectedDoctor.email}</p>
                </div>
                <div>
                  <p className="text-slate-500">Especialidad</p>
                  <p className="font-medium">{selectedDoctor.specialty || "-"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Teléfono</p>
                  <p className="font-medium">{selectedDoctor.phone || "-"}</p>
                </div>
                <div>
                  <p className="text-slate-500">País</p>
                  <p className="font-medium">{selectedDoctor.country || "-"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Provincia</p>
                  <p className="font-medium">{selectedDoctor.province || "-"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Matrícula</p>
                  <p className="font-medium font-mono">{selectedDoctor.license_number || "-"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Entidad Emisora</p>
                  <p className="font-medium">{selectedDoctor.license_issuer || "-"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Fecha Registro</p>
                  <p className="font-medium">{formatDate(selectedDoctor.created_at)}</p>
                </div>
              </div>

              {selectedDoctor.credential_document_url && (
                <div className="pt-4 border-t">
                  <p className="text-slate-500 text-sm mb-2">Documento de Credencial</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`${API_URL}${selectedDoctor.credential_document_url}`, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Ver documento
                  </Button>
                </div>
              )}

              {selectedDoctor.rejection_reason && (
                <div className="pt-4 border-t">
                  <p className="text-slate-500 text-sm mb-2">Motivo de rechazo</p>
                  <p className="text-sm bg-red-50 p-3 rounded border border-red-200 text-red-800">
                    {selectedDoctor.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedDoctor?.doctor_status === 'PENDING_REVIEW' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowDetailModal(false);
                    openRejectModal(selectedDoctor);
                  }}
                >
                  Rechazar
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleApprove(selectedDoctor)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aprobar"}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Solicitud</DialogTitle>
            <DialogDescription>
              Proporciona un motivo para rechazar la solicitud de {selectedDoctor?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="reject-reason">Motivo del rechazo *</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: No se pudo verificar la matrícula profesional..."
              rows={4}
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar Rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDoctors;
